/**
 * Compliance & never list (§G) — typed replacement for the legacy flat-field
 * canvas block, which edited three legacy strings while the completeness score
 * reads the typed `bb_compliance_rules` table (so an operator could fill the
 * section and earn 0 of 15 points). Correct write surface: same store, same
 * Core-token primitives as OffersPanel, direct on `compliance_rules`.
 *
 * §G is human-entered only (enrichment never drafts compliance rows, contract
 * §5). Closes two gaps with no UI/producer before: the `platform_note` kind and
 * the `applies_to` column. Client-side guards below exist because the write
 * layer has none (backend/api/services/brand_bible_v2/entries.py): dedup, kind
 * validity (type-enforced), and trim/length caps all live here before an
 * upsert. Soft delete never resurrects — re-adding text creates a NEW row.
 * Only VERIFIED rules reach generation, so an unverified rule must read NOT YET
 * IN EFFECT, not a neutral "pending" (why this doesn't reuse VerificationRow).
 *
 * ── Explicit-save conversion (bb-phase2-explicit-save-brief-20260810) ─────────
 * §G adopts the explicit-save contract. Two edit classes buffer LOCALLY and
 * commit only on the page's Save button:
 *   Class 1 — typed `bb_compliance_rules` rows. create / patch(applies_to) /
 *     soft-delete buffer as ID-KEYED ops (never a list diff — a stale list
 *     replace wiped a live prod row on §B). A buffered create that is removed
 *     before Save cancels out with no server round-trip.
 *   Class 2 — the three legacy flat fields (words_avoid, services_not_offered,
 *     regulatory_notes) that compose the `brand_restrictions` column. Their
 *     inputs live on BrandBiblePage (passed in via `legacy`); once 'G' joins
 *     CONVERTED_SECTIONS the page STOPS autosaving that column, so save() must
 *     commit them through the page's draft-commit seam (useBbDraftCommit) or
 *     they become silently unsaveable. A null seam FAILS save() loudly.
 * Row-level Verify stays IMMEDIATE — it is an operator act, not an edit, and
 * needs a real server id (buffered creates cannot be verified until saved).
 */
import { useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBrandBibleWizardStore, selectEntries } from '@/stores/brandBibleWizardStore';
import type { BbEntry, ComplianceKind } from '@/features/brand-bible-wizard/types';
import { EnvelopeBadge, ExplicitNoneButton, StepCard, inputClass } from '@/components/brand-bible-wizard/primitives';
import { ComplianceLegacyMirror, type ComplianceLegacyInput } from './ComplianceLegacyMirror';
import { BannedClaimsSuggest, ExclusionsSuggest, TopicsPlatformSuggest } from './ComplianceSuggest';
import {
  useRegisterBbSectionSave,
  useBbDraftCommit,
  type BbSectionSaveRegistration,
  type DirtyFormSaveResult,
} from './save';

const KIND_LABEL: Record<ComplianceKind, string> = {
  banned_claim: 'Banned claim',
  regulated: 'Regulated',
  excluded_service: 'Excluded service',
  excluded_area: 'Excluded area',
  sensitive_topic: 'Sensitive topic',
  platform_note: 'Platform note',
};

/* --------------------------------- Rule state ----------------------------------
 * Five visually distinct states. auto-derived is only the license display row;
 * the rest are computed from a rule's fields. Verification wins over explicit-
 * none — an unverified "none" is inert, so it reads not-yet-binding until verified. */
type RuleState = 'verified' | 'unverified' | 'auto-derived' | 'explicit-none' | 'stale';

const STALE_AFTER_DAYS = 180;

function isStale(verifiedAt: string | null): boolean {
  if (!verifiedAt) return false;
  const ageMs = Date.now() - new Date(verifiedAt).getTime();
  return ageMs > STALE_AFTER_DAYS * 24 * 60 * 60 * 1000;
}

function ruleState(rule: Pick<BbEntry<'compliance_rules'>, 'rule_text' | 'verified_by' | 'verified_at'>): RuleState {
  // Verification is checked BEFORE explicit-none: only verified rows reach the
  // writer, so an unverified sentinel is inert and reads not-yet-binding like any
  // other unverified row. explicit-none is reserved for a verified sentinel.
  const isNone = (rule.rule_text ?? '').trim().toLowerCase() === 'none';
  if (!rule.verified_by) return 'unverified';
  return isStale(rule.verified_at) ? 'stale' : isNone ? 'explicit-none' : 'verified';
}

const STATE_STYLE: Record<RuleState, string> = {
  verified: 'bg-pace-good-light text-pace-good-dark',
  stale: 'bg-pace-warn-light text-pace-warn-dark border border-pace-warn/30',
  unverified: 'border border-destructive/25 bg-destructive/10 text-destructive',
  'explicit-none': 'bg-teal-light text-teal',
  'auto-derived': 'border border-coral/25 bg-coral-light text-coral-dark',
};

// unverified reads as "not yet in effect", never neutral "pending" — a scan of
// the panel must tell instantly which rules are binding.
const STATE_LABEL: Record<RuleState, string> = {
  verified: 'in effect',
  stale: 'stale · reverify',
  unverified: 'not yet in effect',
  'explicit-none': 'recorded · none',
  'auto-derived': 'auto-derived',
};

function RuleStatusBadge({ state }: { state: RuleState }) {
  return (
    <span
      className={cn(
        'rounded-[5px] px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide',
        STATE_STYLE[state],
      )}
    >
      {STATE_LABEL[state]}
    </span>
  );
}

function RuleStatusRow({
  rule,
  canVerify,
  onVerify,
}: {
  rule: BbEntry<'compliance_rules'>;
  canVerify: boolean;
  onVerify: () => void;
}) {
  const state = ruleState(rule);
  const showVerify = canVerify && (state === 'unverified' || state === 'stale');
  return (
    <div className="flex items-center gap-1.5">
      <RuleStatusBadge state={state} />
      {state === 'verified' && rule.verified_at && (
        <span className="text-[11px] text-muted-foreground">{new Date(rule.verified_at).toLocaleDateString()}</span>
      )}
      {showVerify && (
        <button type="button" onClick={onVerify} className="text-[11px] font-semibold text-coral hover:text-coral-hover">
          {state === 'stale' ? 'Re-verify' : 'Verify'}
        </button>
      )}
    </div>
  );
}

function RemoveButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button type="button" title={label} aria-label={label} onClick={onClick} className="text-muted-foreground hover:text-destructive">
      <X className="size-3" />
    </button>
  );
}

const matchesKind = (kind: ComplianceKind | ComplianceKind[], target: ComplianceKind) =>
  (Array.isArray(kind) ? kind : [kind]).includes(target);

function DupWarning({
  kind,
  dupWarning,
}: {
  // Array form covers a group merging >1 kind (Banned claims = banned_claim +
  // regulated); the warning fires on whichever of them it landed on.
  kind: ComplianceKind | ComplianceKind[];
  dupWarning: { kind: ComplianceKind; text: string } | null;
}) {
  if (!dupWarning || !matchesKind(kind, dupWarning.kind)) return null;
  return (
    <p className="px-4 pb-2 text-[11px] text-destructive">
      Already recorded for {KIND_LABEL[dupWarning.kind]}: &ldquo;{dupWarning.text}&rdquo; — not added again.
    </p>
  );
}

function RuleRow({
  rule,
  tag,
  canVerify,
  onVerify,
  onRemove,
}: {
  rule: BbEntry<'compliance_rules'>;
  tag: string;
  canVerify: boolean;
  onVerify: () => void;
  onRemove: () => void;
}) {
  return (
    <li className="flex items-start gap-3 border-b border-border/60 px-4 py-2.5 last:border-b-0">
      <span className="mt-0.5 flex-none rounded-[5px] bg-teal-light px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-teal">
        {tag}
      </span>
      <span className="text-[13.5px] leading-[19px]">{rule.rule_text}</span>
      <span className="ml-auto flex flex-none items-center gap-2.5">
        <RuleStatusRow rule={rule} canVerify={canVerify} onVerify={onVerify} />
        <RemoveButton onClick={onRemove} label={`Remove ${tag}`} />
      </span>
    </li>
  );
}

function PlatformNoteRow({
  rule,
  canVerify,
  onVerify,
  onUpdateAppliesTo,
  onRemove,
}: {
  rule: BbEntry<'compliance_rules'>;
  canVerify: boolean;
  onVerify: () => void;
  onUpdateAppliesTo: (value: string) => void;
  onRemove: () => void;
}) {
  return (
    <li className="flex items-start gap-3 border-b border-border/60 px-4 py-2.5 last:border-b-0">
      <div className="flex-1">
        <div className="text-[13.5px] leading-[19px]">{rule.rule_text}</div>
        <input
          className={cn(inputClass, 'mt-1.5 max-w-[320px] py-1.5 text-xs')}
          defaultValue={rule.applies_to ?? ''}
          placeholder="applies to — e.g. a specific platform or content type"
          onBlur={(e) => {
            if (e.target.value !== (rule.applies_to ?? '')) onUpdateAppliesTo(e.target.value);
          }}
        />
      </div>
      <span className="ml-auto flex flex-none items-center gap-2.5">
        <RuleStatusRow rule={rule} canVerify={canVerify} onVerify={onVerify} />
        <RemoveButton onClick={onRemove} label="Remove platform note" />
      </span>
    </li>
  );
}

// Kind + trimmed/lowercased text match — shared by addRule's dup guard and the
// legacy mirror's already-migrated check so neither can drift.
function ruleExists(rules: BbEntry<'compliance_rules'>[], kind: ComplianceKind, text: string): boolean {
  const norm = text.trim().toLowerCase();
  return rules.some((r) => r.kind === kind && (r.rule_text ?? '').trim().toLowerCase() === norm);
}

// Rule text is tenant-controlled input concatenated into the writer's prompt
// once the injection bridge lands, and the backend enforces no length limit —
// an unbounded rule is both a prompt-injection vector and can crowd out the
// rest of the prompt. Capped here, in the one addRule path every kind funnels
// through.
const MAX_RULE_TEXT_LENGTH = 2000;

function LengthWarning({
  kind,
  lengthWarning,
}: {
  // Array form covers the Banned claims group the same way DupWarning does.
  kind: ComplianceKind | ComplianceKind[];
  lengthWarning: ComplianceKind | null;
}) {
  if (!lengthWarning || !matchesKind(kind, lengthWarning)) return null;
  return (
    <p className="px-4 pb-2 text-[11px] text-destructive">
      Rule text is over {MAX_RULE_TEXT_LENGTH} characters — shorten it before saving.
    </p>
  );
}

/* -------------------------------- Save errors ---------------------------------
 * Every write here can fail once its promise rejects, and on a COMPLIANCE
 * surface that is the worst outcome: the operator believes a prohibition is
 * binding when it is not. Buffered writes now fail at Save time (not at edit
 * time), so a failure surfaces AFTER the operator clicks Save — the failing op
 * stays buffered (kept, re-savable), never dropped. Honest, actionable copy —
 * never a raw error or status code. */
const RULE_NOT_SAVED = "This rule wasn't saved and isn't in effect — your edit was kept, so you can try again.";
const NONE_MARKER_NOT_CLEARED =
  'The rule was saved, but the earlier “none” marker could not be cleared — remove it if it is still shown.';
const RULE_NOT_VERIFIED = "This rule wasn't verified, so it isn't in effect yet — try again.";
const RULE_NOT_REMOVED = "This rule wasn't removed and is still recorded — try again.";
const APPLIES_TO_NOT_SAVED = "The “applies to” change wasn't saved — try again.";
// Class 2 (legacy brand_restrictions fields) — no per-kind home, surfaced in
// the panel-level banner.
const LEGACY_NOT_SAVED =
  "Your words-to-avoid, services-not-offered, and regulatory notes weren't saved — your edits were kept, so you can try again.";
const LEGACY_COMMIT_UNAVAILABLE =
  "Your words-to-avoid, services-not-offered, and regulatory notes couldn't be saved because the save connection isn't ready — nothing was lost. Reload and try again.";

type SaveError = { kind: ComplianceKind; message: string };

function SaveErrorWarning({
  kind,
  saveError,
}: {
  kind: ComplianceKind | ComplianceKind[];
  saveError: SaveError | null;
}) {
  if (!saveError || !matchesKind(kind, saveError.kind)) return null;
  return (
    <p role="alert" className="px-4 pb-2 text-[11px] text-destructive">
      {saveError.message}
    </p>
  );
}

/* -------------------------------- Buffered ops --------------------------------
 * Class-1 (bb_compliance_rules) edits buffer here until Save. Every op is
 * ID-KEYED: a create carries a client tempId, a patch/delete carries the real
 * row id. Ops are NEVER derived from a list diff (a stale list-replace wiped a
 * live prod row on §B). */
interface PendingCreate {
  tempId: string;
  kind: ComplianceKind;
  rule_text: string;
  applies_to?: string;
}
interface RuleOps {
  creates: PendingCreate[];
  patches: Record<string, { applies_to: string }>;
  deletes: string[];
}
const emptyOps = (): RuleOps => ({ creates: [], patches: {}, deletes: [] });
const bufferedCount = (ops: RuleOps): number =>
  ops.creates.length + Object.keys(ops.patches).length + ops.deletes.length;
const isPendingId = (id: string): boolean => id.startsWith('pending:');

// Client tempId source for buffered creates — module-scoped so ids stay unique
// across the panel's lifetime; the value never reaches the server (a buffered
// create sends only {kind, rule_text, applies_to?}, the server assigns the id).
let tempIdCounter = 0;
const nextTempId = (): string => `pending:${++tempIdCounter}`;

function arraysEqual(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}
// Number of the three legacy flat fields that differ from the baseline (0..3).
function legacyDiffCount(cur: ComplianceLegacyInput, base: ComplianceLegacyInput): number {
  let n = 0;
  if (!arraysEqual(cur.wordsAvoid, base.wordsAvoid)) n += 1;
  if (!arraysEqual(cur.servicesNotOffered, base.servicesNotOffered)) n += 1;
  if (cur.regulatoryNotes !== base.regulatoryNotes) n += 1;
  return n;
}

export function CompliancePanel({ canVerify, legacy }: { canVerify: boolean; legacy?: ComplianceLegacyInput }) {
  const upsertEntry = useBrandBibleWizardStore((s) => s.upsertEntry);
  const verifyEntry = useBrandBibleWizardStore((s) => s.verifyEntry);
  const discardEntry = useBrandBibleWizardStore((s) => s.discardEntry);
  // The tenant this component rendered for: every write names it, and the store refuses one for a tenant it has left.
  const bbTenant = useBrandBibleWizardStore((s) => s.tenantId) ?? '';

  // ── Hydration signal. The store flips `loadedTenantId` to the current tenant
  // only once its bible fetch resolves (brandBibleWizardStore.init); until then
  // BrandBiblePage's draft — and therefore the `legacy` prop below — is still
  // seeded from EMPTY_DRAFT ([], [], ''). This is the closest in-panel signal to
  // "the legacy prop has been populated", and it is what gates baseline capture
  // and section registration so a fresh load never reports a phantom Class-2
  // change ("Unsaved: G" with no operator edit).
  //   Caveat (see PR report): the page draft loads from a SEPARATE fetch than
  // the store, so loadedTenantId is a close proxy, not a perfect tracker — the
  // fully reliable signal is a page-side `legacyHydrated` prop. This is the best
  // reachable in-panel signal and strictly fixes the reproduced ordering. */
  const tenantId = useBrandBibleWizardStore((s) => s.tenantId);
  const loadedTenantId = useBrandBibleWizardStore((s) => s.loadedTenantId);
  const hydrated = tenantId !== null && loadedTenantId === tenantId;

  const storeRules = useBrandBibleWizardStore(selectEntries('compliance_rules'));
  const credentials = useBrandBibleWizardStore(selectEntries('credentials'));
  const license = credentials.find((c) => c.kind === 'license' && c.display_required && c.deleted_at === null);

  // ── Class-1 buffer. opsRef mirrors `ops` so save()/isDirty() read the CURRENT
  // buffer synchronously (the registry reads isDirty right after save resolves,
  // before React re-renders); `ops` drives rendering + the registration memo. */
  const [ops, setOps] = useState<RuleOps>(emptyOps);
  const opsRef = useRef<RuleOps>(ops);
  const applyOps = (fn: (prev: RuleOps) => RuleOps) => {
    const next = fn(opsRef.current);
    opsRef.current = next;
    setOps(next);
  };

  // ── Class-2 baseline. The legacy inputs live on BrandBiblePage; `legacy` is
  // the live page-draft value. baseline = the value as of HYDRATION / last save;
  // the diff against it is the Class-2 dirty state + change count.
  //
  // The baseline is captured ONLY once the store has hydrated for the current
  // tenant (`hydrated` above), NEVER on the first render. Capturing on first
  // render froze an EMPTY baseline (draft still EMPTY_DRAFT), so hydration then
  // read as 1 phantom Class-2 change with no operator edit. Gating the capture —
  // and the registration below — on hydration means an empty pre-hydration
  // `legacy` never becomes a baseline, and the diff is taken against the real
  // hydrated value. Keyed by loadedTenantId so a tenant switch re-captures
  // cleanly. This does NOT special-case empty values as clean: a genuinely-empty
  // hydrated tenant captures an empty baseline, so a later edit that CLEARS a
  // field is still a real diff against a populated baseline. */
  const legacyRef = useRef<ComplianceLegacyInput | undefined>(legacy);
  legacyRef.current = legacy;
  const legacyBaselineRef = useRef<ComplianceLegacyInput | undefined>(undefined);
  const baselineTenantRef = useRef<string | null>(null);
  if (hydrated && baselineTenantRef.current !== loadedTenantId) {
    legacyBaselineRef.current = legacy;
    baselineTenantRef.current = loadedTenantId;
  }

  // ── The page-injected flat-field commit seam. Null until the page mounts its
  // provider — a null seam means Class-2 commit is unavailable and save() must
  // fail loudly rather than drop the buffered fields. Read via ref so a new
  // seam identity (page draft changed) never forces a re-registration. */
  const commitSeam = useBbDraftCommit();
  const seamRef = useRef(commitSeam);
  seamRef.current = commitSeam;

  const [dupWarning, setDupWarning] = useState<{ kind: ComplianceKind; text: string } | null>(null);
  const [lengthWarning, setLengthWarning] = useState<ComplianceKind | null>(null);
  const [saveError, setSaveError] = useState<SaveError | null>(null);
  const [saveBanner, setSaveBanner] = useState<string | null>(null);

  // Derived rendered list: store rows (live, minus buffered deletes, with
  // buffered patches applied, minus a "none" sentinel superseded by a pending
  // real create for its kind) PLUS buffered creates rendered as unverified.
  const rules = useMemo<BbEntry<'compliance_rules'>[]>(() => {
    const liveStore = storeRules.filter((r) => r.deleted_at === null);
    const kindsWithPendingReal = new Set(
      ops.creates.filter((c) => c.rule_text.trim().toLowerCase() !== 'none').map((c) => c.kind),
    );
    const kept = liveStore
      .filter((r) => !ops.deletes.includes(r.id))
      .filter((r) => !(r.kind !== undefined && kindsWithPendingReal.has(r.kind) && (r.rule_text ?? '').trim().toLowerCase() === 'none'))
      .map((r) => {
        const patch = ops.patches[r.id];
        return patch ? ({ ...r, ...patch } as BbEntry<'compliance_rules'>) : r;
      });
    const created = ops.creates.map(
      (c): BbEntry<'compliance_rules'> => ({
        id: c.tempId,
        tenant_id: '',
        location_scope: 'all',
        section: 'G',
        source: 'client_edit',
        verified_by: null,
        verified_at: null,
        approved_for_public: false,
        confidence: 'medium',
        effective_from: null,
        effective_to: null,
        version: 0,
        created_at: '',
        updated_at: '',
        deleted_at: null,
        kind: c.kind,
        rule_text: c.rule_text,
        ...(c.applies_to !== undefined ? { applies_to: c.applies_to } : {}),
      }),
    );
    return [...kept, ...created];
  }, [storeRules, ops]);

  const ofKind = (k: ComplianceKind) => rules.filter((r) => r.kind === k);

  const [newClaim, setNewClaim] = useState('');
  // Which kind the "Banned claims" add-input creates — the only group merging
  // two kinds (banned_claim + regulated), so the only add-path needing a kind
  // choice. Defaults to banned_claim, reproducing the old single-kind behaviour.
  const [newClaimKind, setNewClaimKind] = useState<'banned_claim' | 'regulated'>('banned_claim');
  const [newArea, setNewArea] = useState('');
  const [newTopic, setNewTopic] = useState('');
  const [noteDraft, setNoteDraft] = useState({ rule_text: '', applies_to: '' });

  // Clear a stale error for one kind on its next attempt, without wiping one
  // still standing in another group.
  const dismissError = (kind: ComplianceKind) => setSaveError((cur) => (cur?.kind === kind ? null : cur));

  // Verify/unverify are OPERATOR ACTS, not edits — they stay IMMEDIATE and need
  // a real server id, so a buffered (unsaved) create can't be verified yet. A
  // failed verify used to look identical to success; surface it.
  const verify = ({ id, kind = 'banned_claim' }: BbEntry<'compliance_rules'>) => {
    if (isPendingId(id)) return;
    dismissError(kind);
    verifyEntry('compliance_rules', id, undefined, bbTenant).catch(() => setSaveError({ kind, message: RULE_NOT_VERIFIED }));
  };

  // Remove buffers a soft-delete for a saved row (id-keyed, never a list diff);
  // for a buffered create it cancels the create outright — a create-then-delete
  // never round-trips to the server.
  const remove = ({ id, kind = 'banned_claim' }: BbEntry<'compliance_rules'>) => {
    dismissError(kind);
    if (isPendingId(id)) {
      applyOps((prev) => ({ ...prev, creates: prev.creates.filter((c) => c.tempId !== id) }));
      return;
    }
    applyOps((prev) => {
      // Drop any buffered applies_to patch for a row we're now deleting.
      const patches = { ...prev.patches };
      delete patches[id];
      return {
        ...prev,
        patches,
        deletes: prev.deletes.includes(id) ? prev.deletes : [...prev.deletes, id],
      };
    });
  };

  // Returns true once the create is BUFFERED (caller clears its input on true,
  // keeps text on false). The actual write happens on Save.
  const addRule = async (
    kind: ComplianceKind,
    rawText: string,
    extra?: { applies_to?: string },
  ): Promise<boolean> => {
    const text = rawText.trim();
    if (!text) return false;
    if (text.length > MAX_RULE_TEXT_LENGTH) {
      setLengthWarning(kind);
      return false;
    }
    setLengthWarning(null);
    // Dedup against the DERIVED list so a buffered-but-unsaved create counts,
    // and a buffered-deleted row does not (re-adding it is a legitimate new row).
    if (ruleExists(rules, kind, text)) {
      setDupWarning({ kind, text });
      return false;
    }
    setDupWarning(null);
    dismissError(kind);

    applyOps((prev) => {
      // Design spec: "Adding a real rule discards the none-row." A buffered
      // "none" create for this kind cancels immediately (no round-trip); a
      // SAVED "none" sentinel is discarded at Save time, only after the real
      // create is confirmed (see save()).
      const creates =
        text.toLowerCase() !== 'none'
          ? prev.creates.filter((c) => !(c.kind === kind && c.rule_text.trim().toLowerCase() === 'none'))
          : prev.creates;
      return {
        ...prev,
        creates: [...creates, { tempId: nextTempId(), kind, rule_text: text, ...(extra?.applies_to ? { applies_to: extra.applies_to } : {}) }],
      };
    });
    return true;
  };

  const submitNote = () => {
    const appliesTo = noteDraft.applies_to.trim();
    // applies_to is the same unbounded injection surface as rule_text — cap it too.
    if (appliesTo.length > MAX_RULE_TEXT_LENGTH) return setLengthWarning('platform_note');
    void addRule('platform_note', noteDraft.rule_text, appliesTo ? { applies_to: appliesTo } : undefined).then((ok) => {
      if (ok) setNoteDraft({ rule_text: '', applies_to: '' });
    });
  };

  // The scope-tag edit buffers a patch (saved row) or updates the pending
  // create — it never writes immediately, and never skips the length guard.
  const updateAppliesTo = ({ id }: BbEntry<'compliance_rules'>, rawValue: string) => {
    const value = rawValue.trim();
    if (value.length > MAX_RULE_TEXT_LENGTH) return setLengthWarning('platform_note');
    setLengthWarning(null);
    dismissError('platform_note');
    if (isPendingId(id)) {
      applyOps((prev) => ({
        ...prev,
        creates: prev.creates.map((c) => (c.tempId === id ? { ...c, applies_to: value } : c)),
      }));
      return;
    }
    applyOps((prev) => ({ ...prev, patches: { ...prev.patches, [id]: { applies_to: value } } }));
  };

  // ── Save-registry wiring (Class 1 + Class 2). isDirty/changeCount/save/discard
  // read the CURRENT refs so they stay correct regardless of render timing. */
  const isLegacyDirtyNow = () => {
    const cur = legacyRef.current;
    const base = legacyBaselineRef.current;
    if (!cur || !base) return false;
    return legacyDiffCount(cur, base) > 0;
  };
  const legacyChangeCountNow = () => {
    const cur = legacyRef.current;
    const base = legacyBaselineRef.current;
    if (!cur || !base) return 0;
    return legacyDiffCount(cur, base);
  };
  const isDirtyNow = () => bufferedCount(opsRef.current) > 0 || isLegacyDirtyNow();
  const changeCountNow = () => bufferedCount(opsRef.current) + legacyChangeCountNow();

  const save = async (): Promise<DirtyFormSaveResult> => {
    const snapshot = opsRef.current;
    // Read the store fresh at each step — awaited writes mutate it between ops.
    const liveRules = () =>
      (useBrandBibleWizardStore.getState().entries.compliance_rules ?? []).filter((r) => r.deleted_at === null);

    const remainingCreates: PendingCreate[] = [];
    const remainingPatches: Record<string, { applies_to: string }> = {};
    let ruleOpFailed = false;
    let firstOpError: SaveError | null = null;
    let noneMarkerError: SaveError | null = null;

    // 1. Deletes — re-check presence at commit time (another writer may have
    //    removed the row); the store already renders 404 as success. A FAILED
    //    delete drops its buffered op so the still-recorded row reappears (its
    //    data is safe on the server — unlike a create/patch, nothing is lost by
    //    not re-buffering it), matching the "wasn't removed, still recorded" copy.
    for (const id of snapshot.deletes) {
      if (!liveRules().some((r) => r.id === id)) continue;
      const kind = liveRules().find((r) => r.id === id)?.kind ?? 'banned_claim';
      try {
        await discardEntry('compliance_rules', id, bbTenant);
      } catch {
        ruleOpFailed = true;
        firstOpError ??= { kind, message: RULE_NOT_REMOVED };
      }
    }

    // 2. Patches (applies_to) — drop a patch whose row is gone; a failed patch
    //    stays buffered (its value lives only here, so a retry must keep it).
    for (const [id, patch] of Object.entries(snapshot.patches)) {
      if (!liveRules().some((r) => r.id === id)) continue;
      try {
        await upsertEntry('compliance_rules', patch, id, bbTenant);
      } catch {
        remainingPatches[id] = patch;
        ruleOpFailed = true;
        firstOpError ??= { kind: 'platform_note', message: APPLIES_TO_NOT_SAVED };
      }
    }

    // 3. Creates — then discard a saved "none" sentinel for the kind, but only
    //    AFTER the real create is confirmed (a failed create must not strand the
    //    operator with neither the "none" answer nor the new rule).
    for (const c of snapshot.creates) {
      try {
        await upsertEntry('compliance_rules', {
          kind: c.kind,
          rule_text: c.rule_text,
          ...(c.applies_to !== undefined ? { applies_to: c.applies_to } : {}),
        }, undefined, bbTenant);
      } catch {
        remainingCreates.push(c);
        ruleOpFailed = true;
        firstOpError ??= { kind: c.kind, message: RULE_NOT_SAVED };
        continue;
      }
      if (c.rule_text.trim().toLowerCase() !== 'none') {
        const sentinel = liveRules().find(
          (r) => r.kind === c.kind && (r.rule_text ?? '').trim().toLowerCase() === 'none',
        );
        if (sentinel) {
          try {
            await discardEntry('compliance_rules', sentinel.id, bbTenant);
          } catch {
            // The rule itself DID save; only the stale "none" cleanup failed.
            noneMarkerError ??= { kind: c.kind, message: NONE_MARKER_NOT_CLEARED };
          }
        }
      }
    }

    // 4. Class-2 legacy flat fields, through the page draft-commit seam. A null
    //    seam fails loudly; the three fields compose brand_restrictions on the
    //    page ({...draft, ...fields} → buildBrandBiblePayload).
    let legacyOk = true;
    let legacyErrMsg: string | null = null;
    if (isLegacyDirtyNow()) {
      const seam = seamRef.current;
      const cur = legacyRef.current!;
      if (!seam) {
        legacyOk = false;
        legacyErrMsg = LEGACY_COMMIT_UNAVAILABLE;
      } else {
        try {
          const ok = await seam(
            {
              words_avoid: cur.wordsAvoid,
              services_not_offered: cur.servicesNotOffered,
              regulatory_notes: cur.regulatoryNotes,
            },
            'G',
          );
          if (ok) legacyBaselineRef.current = cur;
          else {
            legacyOk = false;
            legacyErrMsg = LEGACY_NOT_SAVED;
          }
        } catch {
          legacyOk = false;
          legacyErrMsg = LEGACY_NOT_SAVED;
        }
      }
    }

    // Commit the buffer to only the ops still pending (succeeded ops are dropped
    // so a retry never redoes landed work; a failed delete is dropped too, so its
    // still-recorded row reappears).
    const nextOps: RuleOps = { creates: remainingCreates, patches: remainingPatches, deletes: [] };
    opsRef.current = nextOps;
    setOps(nextOps);

    // A confirmed create's "none" cleanup failure is a warning, not a failure —
    // the rule saved. It rides in the per-kind inline error only.
    setSaveError(firstOpError ?? noneMarkerError);
    setSaveBanner(legacyOk ? null : legacyErrMsg);

    if (!legacyOk || ruleOpFailed) {
      const errorText = !legacyOk ? legacyErrMsg! : firstOpError?.message ?? 'Some changes were not saved.';
      return { success: false, error: errorText };
    }
    return true;
  };

  // discard() clears the Class-1 buffer (rules revert to store state). It does
  // NOT touch the Class-2 baseline: the legacy inputs are page-owned, so the
  // panel cannot revert them, and silently marking them clean would strand an
  // edit that can no longer autosave (silent data loss — the exact hazard this
  // conversion exists to prevent). A Class-2 edit clears only by Save, or by the
  // operator typing the page input back to its saved value. (See report.)
  const discard = () => {
    applyOps(() => emptyOps());
    setDupWarning(null);
    setLengthWarning(null);
    setSaveError(null);
    setSaveBanner(null);
  };

  const totalChangeCount = bufferedCount(ops) + (legacy && legacyBaselineRef.current ? legacyDiffCount(legacy, legacyBaselineRef.current) : 0);
  const registration = useMemo<BbSectionSaveRegistration>(
    () => ({ isDirty: isDirtyNow, save, discard, changeCount: changeCountNow }),
    // Re-registers (→ notify → reactive Save button / Unsaved indicator) only
    // when the visible change count crosses; the methods read refs, so they are
    // never stale despite the narrow dep list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [totalChangeCount],
  );
  // Gate registration on hydration: an unregistered section is clean by the
  // registry's design, so nothing reports dirty while the legacy prop is still
  // seeding from EMPTY_DRAFT. Once hydrated (baseline captured above), register.
  // A Class-1 buffered edit made before hydration therefore reports dirty only
  // once hydration lands — late, never lost (buffered ops survive in opsRef).
  useRegisterBbSectionSave('G', hydrated ? registration : null);

  return (
    <div className="flex flex-col gap-3.5">
      {saveBanner && (
        <div role="alert" className="rounded-[10px] border border-destructive/30 bg-destructive/10 px-4 py-3 text-[13px] font-medium text-destructive">
          {saveBanner}
        </div>
      )}
      <div className="flex items-center gap-3 rounded-[10px] border border-border/60 bg-teal-light px-4 py-3">
        <span className="flex-none rounded-[5px] border border-teal/30 bg-warm-bg px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-teal-dark">
          Not yet enforced
        </span>
        <div>
          <div className="text-[13px] font-semibold text-teal-dark">Rules here are recorded and scored today — delivery to the writer is not yet live.</div>
          <div className="text-xs text-muted-foreground">Design: once connected, rules inject on every call instead of being retrieved by similarity search, so a rule can never lose a ranking.</div>
        </div>
      </div>

      <StepCard title="Banned claims" headerRight={<span className="text-xs text-muted-foreground">scores at 1+ · or record explicit &ldquo;none&rdquo;</span>}>
        <ul>
          {[...ofKind('banned_claim'), ...ofKind('regulated')].map((r) => (
            <RuleRow
              key={r.id}
              rule={r}
              tag={r.kind === 'regulated' ? 'regulated' : 'claim'}
              canVerify={canVerify && !isPendingId(r.id)}
              onVerify={() => verify(r)}
              onRemove={() => remove(r)}
            />
          ))}
        </ul>
        <div className="flex gap-2 p-4">
          <div className="flex flex-none items-center gap-1 rounded-lg border border-input bg-warm-bg p-1">
            {(['banned_claim', 'regulated'] as const).map((k) => (
              <button
                key={k}
                type="button"
                aria-pressed={newClaimKind === k}
                onClick={() => setNewClaimKind(k)}
                className={cn(
                  'rounded-[6px] px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-wide',
                  newClaimKind === k ? 'bg-coral text-white' : 'text-teal hover:bg-teal-light',
                )}
              >
                {KIND_LABEL[k]}
              </button>
            ))}
          </div>
          <input
            className={inputClass}
            placeholder={
              newClaimKind === 'regulated'
                ? 'e.g. a requirement tied to a credential, license, or regulatory approval'
                : 'e.g. a claim that needs a qualifier or third-party proof before publishing'
            }
            value={newClaim}
            onChange={(e) => setNewClaim(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                void addRule(newClaimKind, newClaim).then((ok) => {
                  if (ok) setNewClaim('');
                });
              }
            }}
          />
          <ExplicitNoneButton
            label={'No banned claims — record "none"'}
            disabled={ofKind('banned_claim').length > 0}
            onClick={() => void addRule('banned_claim', 'none')}
          />
          {/* Field-labelled: a "Banned claim" chip creates banned_claim, a "Regulated" chip creates regulated. */}
          <BannedClaimsSuggest
            bannedExisting={ofKind('banned_claim').map((r) => r.rule_text ?? '')}
            regulatedExisting={ofKind('regulated').map((r) => r.rule_text ?? '')}
            onAcceptRule={(kind, t) => void addRule(kind, t)}
          />
        </div>
        <DupWarning kind={['banned_claim', 'regulated']} dupWarning={dupWarning} />
        <LengthWarning kind={['banned_claim', 'regulated']} lengthWarning={lengthWarning} />
        <SaveErrorWarning kind={['banned_claim', 'regulated']} saveError={saveError} />
      </StepCard>

      {license && (
        <StepCard title="License display rule" headerRight={<EnvelopeBadge kind="auto">auto-derived</EnvelopeBadge>}>
          <div className="p-4">
            <div className="text-[13.5px]">
              &ldquo;Licensed &amp; insured&rdquo; must cite <span className="font-mono">{license.number}</span> on service pages
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Written automatically because the license record is marked display-required. Edits happen on the license, not here — this
              row is never a §G row of its own.
            </p>
          </div>
        </StepCard>
      )}

      <StepCard
        title="Excluded services & areas"
        headerRight={<span className="text-xs text-muted-foreground">agents must never generate demand the client can&rsquo;t fulfill</span>}
      >
        <div className="flex flex-col gap-3.5 p-4">
          {/* Group-level: "Excluded service" chips create excluded_service, "Excluded area" chips create excluded_area. */}
          <div className="flex justify-end">
            <ExclusionsSuggest
              serviceExisting={ofKind('excluded_service').map((r) => r.rule_text ?? '')}
              areaExisting={ofKind('excluded_area').map((r) => r.rule_text ?? '')}
              onAcceptRule={(kind, t) => void addRule(kind, t)}
            />
          </div>
          <div>
            <div className="mb-1.5 text-xs font-semibold text-teal-dark">Excluded services</div>
            <div className="flex flex-wrap gap-2">
              {ofKind('excluded_service').map((r) => (
                <button
                  key={r.id}
                  type="button"
                  title="Remove"
                  onClick={() => remove(r)}
                  className="rounded-full border border-destructive/25 bg-destructive/10 px-3 py-1 text-xs font-medium text-destructive line-through"
                >
                  {r.rule_text}
                </button>
              ))}
              <input
                className="rounded-full border border-dashed border-input bg-warm-bg px-3 py-1 text-xs text-teal outline-none focus:border-coral"
                placeholder="+ add"
                onKeyDown={(e) => {
                  const el = e.target as HTMLInputElement;
                  const v = el.value.trim();
                  if (e.key === 'Enter' && v) {
                    void addRule('excluded_service', v).then((ok) => {
                      if (ok) el.value = '';
                    });
                  }
                }}
              />
            </div>
            <DupWarning kind="excluded_service" dupWarning={dupWarning} />
            <LengthWarning kind="excluded_service" lengthWarning={lengthWarning} />
            <SaveErrorWarning kind="excluded_service" saveError={saveError} />
          </div>
          <div>
            <div className="mb-1.5 flex items-baseline">
              <span className="text-xs font-semibold text-teal-dark">Excluded areas</span>
              <span className="ml-auto text-[11px] text-muted-foreground">geography agents must never target</span>
            </div>
            {ofKind('excluded_area').map((r) => (
              <div key={r.id} className="mb-1.5 flex items-center gap-2">
                <div className={cn(inputClass, 'flex-1')}>{r.rule_text}</div>
                <RuleStatusRow rule={r} canVerify={canVerify && !isPendingId(r.id)} onVerify={() => verify(r)} />
                <RemoveButton onClick={() => remove(r)} label="Remove excluded area" />
              </div>
            ))}
            <input
              className={inputClass}
              value={newArea}
              onChange={(e) => setNewArea(e.target.value)}
              placeholder="e.g. a region outside current service coverage — must never be targeted"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newArea.trim()) {
                  void addRule('excluded_area', newArea).then((ok) => {
                    if (ok) setNewArea('');
                  });
                }
              }}
            />
            <DupWarning kind="excluded_area" dupWarning={dupWarning} />
            <LengthWarning kind="excluded_area" lengthWarning={lengthWarning} />
            <SaveErrorWarning kind="excluded_area" saveError={saveError} />
          </div>
          <div className="flex gap-2.5">
            <ExplicitNoneButton
              label={'No excluded services — record "none"'}
              disabled={ofKind('excluded_service').length > 0}
              onClick={() => void addRule('excluded_service', 'none')}
            />
            <ExplicitNoneButton
              label={'No excluded areas — record "none"'}
              disabled={ofKind('excluded_area').length > 0}
              onClick={() => void addRule('excluded_area', 'none')}
            />
          </div>
        </div>
      </StepCard>

      <StepCard
        title="Sensitive topics"
        headerRight={
          /* Covers this group AND platform notes below: "Sensitive topic"/"Platform note"
             chips create their kinds; "Applies-to scope" fills the note-scope input, no row. */
          <TopicsPlatformSuggest
            topicExisting={ofKind('sensitive_topic').map((r) => r.rule_text ?? '')}
            noteExisting={ofKind('platform_note').map((r) => r.rule_text ?? '')}
            appliesToExisting={ofKind('platform_note').map((r) => r.applies_to ?? '').filter(Boolean)}
            onAcceptRule={(kind, t) => void addRule(kind, t)}
            onAcceptAppliesTo={(v) => setNoteDraft((d) => ({ ...d, applies_to: v }))}
          />
        }
      >
        <ul>
          {ofKind('sensitive_topic').map((r) => (
            <RuleRow
              key={r.id}
              rule={r}
              tag="history"
              canVerify={canVerify && !isPendingId(r.id)}
              onVerify={() => verify(r)}
              onRemove={() => remove(r)}
            />
          ))}
        </ul>
        <div className="p-4">
          <input
            className={inputClass}
            value={newTopic}
            onChange={(e) => setNewTopic(e.target.value)}
            placeholder="e.g. a past name change, incident, or history agents must never bring up"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newTopic.trim()) {
                void addRule('sensitive_topic', newTopic).then((ok) => {
                  if (ok) setNewTopic('');
                });
              }
            }}
          />
          <DupWarning kind="sensitive_topic" dupWarning={dupWarning} />
          <LengthWarning kind="sensitive_topic" lengthWarning={lengthWarning} />
          <SaveErrorWarning kind="sensitive_topic" saveError={saveError} />
        </div>
      </StepCard>

      <StepCard
        title="Platform notes"
        headerRight={<span className="text-xs text-muted-foreground">a rule that only applies on one platform or content type</span>}
      >
        <ul>
          {ofKind('platform_note').map((r) => (
            <PlatformNoteRow
              key={r.id}
              rule={r}
              canVerify={canVerify && !isPendingId(r.id)}
              onVerify={() => verify(r)}
              onRemove={() => remove(r)}
              onUpdateAppliesTo={(value) => updateAppliesTo(r, value)}
            />
          ))}
        </ul>
        <div className="flex flex-col gap-2 p-4">
          <input
            className={inputClass}
            placeholder="e.g. a rule that applies on one channel or content type but not everywhere"
            value={noteDraft.rule_text}
            onChange={(e) => setNoteDraft((d) => ({ ...d, rule_text: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitNote();
            }}
          />
          <div className="flex gap-2">
            <input
              className={cn(inputClass, 'max-w-[320px]')}
              placeholder="applies to — e.g. a specific platform or content type"
              value={noteDraft.applies_to}
              onChange={(e) => setNoteDraft((d) => ({ ...d, applies_to: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitNote();
              }}
            />
            <button
              type="button"
              onClick={submitNote}
              className="flex-none rounded-lg bg-coral px-3.5 py-2 text-xs font-semibold text-white hover:bg-coral-hover"
            >
              Add note
            </button>
          </div>
          <DupWarning kind="platform_note" dupWarning={dupWarning} />
          <LengthWarning kind="platform_note" lengthWarning={lengthWarning} />
          <SaveErrorWarning kind="platform_note" saveError={saveError} />
        </div>
      </StepCard>

      <ComplianceLegacyMirror
        legacy={legacy}
        isMigrated={(kind, text) => ruleExists(rules, kind, text)}
        onMigrate={(kind, text) => addRule(kind, text)}
      />
    </div>
  );
}
